import React, { useState, useEffect, useCallback } from 'react';
import EmptyState from '../../components/EmptyState';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardActions,
  Grid,
  Stack,
  Divider,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Campaign as CampaignIcon,
  PriorityHigh as UrgentIcon,
  NotificationImportant as ImportantIcon,
  NotificationsNone as NormalIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { principalAPI } from '../../services/api';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const PRIORITY_CONFIG = {
  normal: { label: 'Normal', color: 'default', icon: <NormalIcon fontSize="small" /> },
  important: { label: 'Important', color: 'warning', icon: <ImportantIcon fontSize="small" /> },
  urgent: { label: 'Urgent', color: 'error', icon: <UrgentIcon fontSize="small" /> }
};

const EMPTY_FORM = { title: '', content: '', priority: 'normal', expires_at: '' };

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(dateStr);
};

const PrincipalAnnouncements = () => {
  const { user, logout } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPriority, setFilterPriority] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Delete confirm state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const res = await principalAPI.getAnnouncements();
      setAnnouncements(res.data.announcements || []);
    } catch (error) {
      showSnackbar('Failed to load announcements', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleOpenEdit = (announcement) => {
    setEditingId(announcement.id);
    setForm({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      expires_at: announcement.expires_at
        ? new Date(announcement.expires_at).toISOString().split('T')[0]
        : ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      showSnackbar('Title and content are required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        priority: form.priority,
        expires_at: form.expires_at || null
      };
      if (editingId) {
        await principalAPI.updateAnnouncement(editingId, payload);
        showSnackbar('Announcement updated successfully');
      } else {
        await principalAPI.createAnnouncement(payload);
        showSnackbar('Announcement created successfully');
      }
      handleCloseDialog();
      fetchAnnouncements();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save announcement', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = (id) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await principalAPI.deleteAnnouncement(deletingId);
      showSnackbar('Announcement deleted');
      setDeleteDialogOpen(false);
      setDeletingId(null);
      fetchAnnouncements();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to delete announcement', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = filterPriority === 'all'
    ? announcements
    : announcements.filter(a => a.priority === filterPriority);

  const isExpired = (a) => a.expires_at && new Date(a.expires_at) < new Date();

  return (
    <>
        <Box
          component="main"
          sx={{ flex: 1, minWidth: 0, bgcolor: '#F8FAFC', p: { xs: 2, sm: 3, md: 4 } }}
        >
          {/* Page Header */}
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} mb={3} gap={2}>
            <Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                Announcements
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Post notices and updates for your teachers and staff
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenCreate}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              New Announcement
            </Button>
          </Box>

          {/* Filter */}
          <Box mb={3}>
            <ToggleButtonGroup
              value={filterPriority}
              exclusive
              onChange={(_, v) => v && setFilterPriority(v)}
              size="small"
            >
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>All</ToggleButton>
              <ToggleButton value="normal" sx={{ textTransform: 'none', px: 2 }}>Normal</ToggleButton>
              <ToggleButton value="important" sx={{ textTransform: 'none', px: 2 }}>Important</ToggleButton>
              <ToggleButton value="urgent" sx={{ textTransform: 'none', px: 2 }}>Urgent</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Content */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={CampaignIcon}
              variant={filterPriority === 'all' ? 'empty' : 'no-results'}
              title={filterPriority === 'all' ? 'No announcements yet' : `No ${filterPriority} announcements`}
              description={filterPriority === 'all' ? 'Click "New Announcement" to post your first notice.' : undefined}
              actionLabel={filterPriority === 'all' ? 'New Announcement' : undefined}
              onAction={filterPriority === 'all' ? handleOpenCreate : undefined}
            />
          ) : (
            <Grid container spacing={2}>
              {filtered.map((a) => {
                const pc = PRIORITY_CONFIG[a.priority] || PRIORITY_CONFIG.normal;
                const expired = isExpired(a);
                return (
                  <Grid item xs={12} md={6} lg={4} key={a.id}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        opacity: (!a.is_active || expired) ? 0.6 : 1,
                        border: '1px solid',
                        borderColor: a.priority === 'urgent' ? 'error.light'
                          : a.priority === 'important' ? 'warning.light'
                          : 'divider',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                      }}
                    >
                      <CardContent sx={{ flex: 1, pb: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1} flexWrap="wrap" gap={0.5}>
                          <Chip
                            icon={pc.icon}
                            label={pc.label}
                            color={pc.color}
                            size="small"
                            variant="outlined"
                          />
                          {!a.is_active && (
                            <Chip label="Archived" size="small" color="default" />
                          )}
                          {expired && (
                            <Chip label="Expired" size="small" color="default" />
                          )}
                        </Stack>

                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                          {a.title}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {a.content}
                        </Typography>
                      </CardContent>

                      <Divider />

                      <CardActions sx={{ px: 2, py: 1, justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.disabled">
                          {timeAgo(a.created_at)}
                          {a.expires_at && ` · Expires ${formatDate(a.expires_at)}`}
                        </Typography>
                        <Stack direction="row" spacing={0}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenEdit(a)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteConfirm(a.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

      {/* Create / Edit Dialog */}
      <MobileFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={editingId ? 'Edit Announcement' : 'New Announcement'}
        actions={
          <>
            <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={20} /> : editingId ? 'Save Changes' : 'Post Announcement'}
            </Button>
          </>
        }
      >
        <Stack spacing={2.5} pt={0.5}>
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
            required
            fullWidth
            inputProps={{ maxLength: 255 }}
            helperText={`${form.title.length}/255`}
          />
          <TextField
            label="Content"
            value={form.content}
            onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
            required
            fullWidth
            multiline
            rows={5}
            placeholder="Write your announcement here..."
          />
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={form.priority}
              label="Priority"
              onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))}
            >
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="important">Important</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Expiry Date (optional)"
            type="date"
            value={form.expires_at}
            onChange={(e) => setForm(f => ({ ...f, expires_at: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Leave blank for no expiry. After this date, teachers won't see the announcement."
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
          />
        </Stack>
      </MobileFormDialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Delete Announcement</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this announcement? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PrincipalAnnouncements;
